import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Settings,
  DollarSign,
  Target,
  Save,
  RefreshCw,
  Calendar,
  Percent
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const SettingsManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [pricing, setPricing] = useState({
    single_ticket_price: 50,
    bundle_price: 100,
    bundle_quantity: 3
  });
  
  const [campaign, setCampaign] = useState({
    goal: 500000,
    start_date: '2026-03-01',
    end_date: '2026-12-31',
    total_revenue: 0,
    total_tickets_sold: 0
  });

  const token = localStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers,
        withCredentials: true
      });
      
      if (response.data.ticket_pricing) {
        setPricing(response.data.ticket_pricing);
      }
      
      if (response.data.campaign) {
        setCampaign({
          ...campaign,
          ...response.data.campaign,
          start_date: response.data.campaign.start_date?.split('T')[0] || '2026-03-01',
          end_date: response.data.campaign.end_date?.split('T')[0] || '2026-12-31'
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/pricing`, pricing, {
        headers,
        withCredentials: true
      });
      toast.success('Prix mis à jour');
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCampaign = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/campaign`, {
        goal: campaign.goal,
        start_date: campaign.start_date,
        end_date: campaign.end_date
      }, {
        headers,
        withCredentials: true
      });
      toast.success('Paramètres de campagne mis à jour');
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const progressPercent = campaign.goal > 0 
    ? ((campaign.total_revenue || 0) / campaign.goal * 100).toFixed(1) 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Pricing Settings */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Prix des Billets
          </h2>
          <Button onClick={handleSavePricing} disabled={saving} className="bg-green-600 hover:bg-green-700">
            <Save className="w-4 h-4 mr-2" />
            Sauvegarder
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">Prix unitaire ($)</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="number"
                value={pricing.single_ticket_price}
                onChange={(e) => setPricing({ ...pricing, single_ticket_price: parseFloat(e.target.value) || 0 })}
                className="pl-10 text-2xl font-bold"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">Prix pour 1 billet</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">Prix du lot ($)</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="number"
                value={pricing.bundle_price}
                onChange={(e) => setPricing({ ...pricing, bundle_price: parseFloat(e.target.value) || 0 })}
                className="pl-10 text-2xl font-bold"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">Prix pour le lot</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">Quantité du lot</label>
            <Input
              type="number"
              value={pricing.bundle_quantity}
              onChange={(e) => setPricing({ ...pricing, bundle_quantity: parseInt(e.target.value) || 0 })}
              className="text-2xl font-bold"
            />
            <p className="text-xs text-gray-500 mt-2">Nombre de billets dans le lot</p>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>Économie:</strong> ${(pricing.single_ticket_price * pricing.bundle_quantity - pricing.bundle_price).toFixed(2)} 
            ({((1 - pricing.bundle_price / (pricing.single_ticket_price * pricing.bundle_quantity)) * 100).toFixed(0)}% de réduction)
          </p>
        </div>
      </div>

      {/* Campaign Settings */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Paramètres de la Campagne
          </h2>
          <Button onClick={handleSaveCampaign} disabled={saving} className="bg-primary">
            <Save className="w-4 h-4 mr-2" />
            Sauvegarder
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-emerald-700">Progression</span>
            <span className="text-lg font-bold text-emerald-700">{progressPercent}%</span>
          </div>
          <div className="w-full bg-emerald-200 rounded-full h-4">
            <div 
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-4 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, progressPercent)}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-sm text-emerald-600">
            <span>${(campaign.total_revenue || 0).toLocaleString()} collectés</span>
            <span>Objectif: ${campaign.goal.toLocaleString()}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Objectif ($)</label>
            <div className="relative">
              <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="number"
                value={campaign.goal}
                onChange={(e) => setCampaign({ ...campaign, goal: parseFloat(e.target.value) || 0 })}
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Date de début</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="date"
                value={campaign.start_date}
                onChange={(e) => setCampaign({ ...campaign, start_date: e.target.value })}
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Date de fin</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="date"
                value={campaign.end_date}
                onChange={(e) => setCampaign({ ...campaign, end_date: e.target.value })}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-900">{campaign.total_tickets_sold || 0}</p>
            <p className="text-xs text-gray-500">Billets vendus</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">${(campaign.total_revenue || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500">Revenus</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">${(campaign.goal - (campaign.total_revenue || 0)).toLocaleString()}</p>
            <p className="text-xs text-gray-500">Restant</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-purple-600">{progressPercent}%</p>
            <p className="text-xs text-gray-500">Atteint</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SettingsManager;
